/**
 * AvailabilityService — Cross-references Menu data with Library data
 * to determine effective option availability (Local × Master).
 *
 * Rules:
 *   Display = Local.isActive && Master.isAvailable
 *   - Local.isActive  = !option.hidden (per-item override)
 *   - Master.isAvailable = option's libraryOptionId NOT in library's globalHiddenOptionIds
 */

import type { LibraryModifier, MenuItemOption, OptionGroup, MenuProfile, MenuCategory, MenuItem } from '../types';
import { getRepository } from '../../../config/repository';

// ─── Types ──────────────────────────────────────────────────────────────────────

export interface OptionAvailability {
  /** Final display state: true = show, false = hide */
  visible: boolean;
  /** Hidden locally (per-item level) */
  localHidden: boolean;
  /** Hidden globally via Library master toggle */
  globalHidden: boolean;
}

export interface AvailabilityIndex {
  /** Set of globally hidden library option IDs (across all library modifiers) */
  globalHiddenSet: Set<string>;
  /** Map of libraryModId → LibraryModifier for quick lookup */
  libraryMap: Map<string, LibraryModifier>;
}

// ─── Build Index ────────────────────────────────────────────────────────────────

/** Build an in-memory index from all library modifiers for fast availability checks */
export function buildAvailabilityIndex(libraryMods: LibraryModifier[]): AvailabilityIndex {
  const globalHiddenSet = new Set<string>();
  const libraryMap = new Map<string, LibraryModifier>();

  for (const mod of libraryMods) {
    libraryMap.set(mod.id, mod);
    if (mod.globalHiddenOptionIds) {
      for (const optId of mod.globalHiddenOptionIds) {
        globalHiddenSet.add(optId);
      }
    }
  }

  return { globalHiddenSet, libraryMap };
}

// ─── Check Single Option ────────────────────────────────────────────────────────

/** Check availability of a single option within a linked group */
export function checkOptionAvailability(
  option: MenuItemOption,
  group: OptionGroup,
  index: AvailabilityIndex,
): OptionAvailability {
  const localHidden = option.hidden === true;

  // Only check global if group is linked and option has a libraryOptionId
  let globalHidden = false;
  if (group.linkedLibraryId && option.libraryOptionId) {
    globalHidden = index.globalHiddenSet.has(option.libraryOptionId);
  }

  return {
    visible: !localHidden && !globalHidden,
    localHidden,
    globalHidden,
  };
}

// ─── Check All Options in a Group ───────────────────────────────────────────────

/** Returns map of optionId → OptionAvailability for all options in a group */
export function checkGroupAvailability(
  group: OptionGroup,
  index: AvailabilityIndex,
): Map<string, OptionAvailability> {
  const result = new Map<string, OptionAvailability>();
  for (const opt of group.options) {
    result.set(opt.id, checkOptionAvailability(opt, group, index));
  }
  return result;
}

// ─── Async Loader ───────────────────────────────────────────────────────────────

/** Load all library modifiers and build the availability index */
export async function loadAvailabilityIndex(brandId?: string): Promise<AvailabilityIndex> {
  const repo = getRepository();
  const allMods = await repo.getAll<LibraryModifier>('modifier-library');
  const scoped = brandId ? allMods.filter(m => !m.brandId || m.brandId === brandId) : allMods;
  return buildAvailabilityIndex(scoped);
}

// ─── Bulk Toggle ────────────────────────────────────────────────────────────────

/** Toggle global availability of a single option within a library modifier */
export function toggleGlobalOptionAvailability(
  mod: LibraryModifier,
  optionId: string,
): LibraryModifier {
  const hidden = new Set(mod.globalHiddenOptionIds || []);
  if (hidden.has(optionId)) {
    hidden.delete(optionId);
  } else {
    hidden.add(optionId);
  }
  return { ...mod, globalHiddenOptionIds: [...hidden], updatedAt: Date.now() };
}

/** Bulk-hide all options matching a name across all library modifiers */
export function bulkToggleByName(
  mods: LibraryModifier[],
  optionName: string,
  hide: boolean,
): LibraryModifier[] {
  const nameLC = optionName.toLowerCase().trim();
  return mods.map(mod => {
    const hidden = new Set(mod.globalHiddenOptionIds || []);
    let changed = false;
    for (const opt of mod.options) {
      if (opt.nameEN.toLowerCase().trim() === nameLC) {
        if (hide && !hidden.has(opt.id)) { hidden.add(opt.id); changed = true; }
        if (!hide && hidden.has(opt.id)) { hidden.delete(opt.id); changed = true; }
      }
    }
    return changed ? { ...mod, globalHiddenOptionIds: [...hidden], updatedAt: Date.now() } : mod;
  });
}

// ─── Search Across All Library Options ──────────────────────────────────────────

export interface LibraryOptionMatch {
  modifier: LibraryModifier;
  option: MenuItemOption;
  isGloballyHidden: boolean;
}

/** Search all library options by name, returning grouped matches */
export function searchLibraryOptions(
  mods: LibraryModifier[],
  query: string,
): LibraryOptionMatch[] {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  const matches: LibraryOptionMatch[] = [];
  for (const mod of mods) {
    const hiddenSet = new Set(mod.globalHiddenOptionIds || []);
    for (const opt of mod.options) {
      if (opt.nameEN.toLowerCase().includes(q) || opt.nameAR.toLowerCase().includes(q)) {
        matches.push({ modifier: mod, option: opt, isGloballyHidden: hiddenSet.has(opt.id) });
      }
    }
  }
  return matches;
}

// ─── Linked Products Lookup ─────────────────────────────────────────────────────

export interface LinkedProductInfo {
  profileId: string;
  profileName: string;
  categoryId: string;
  categoryName: string;
  itemId: string;
  itemName: string;
}

/** Find all products across all profiles that use a given library modifier */
export function getLinkedProducts(
  libraryModId: string,
  profiles: MenuProfile[],
): LinkedProductInfo[] {
  const results: LinkedProductInfo[] = [];
  for (const p of profiles) {
    for (const cat of p.categories) {
      for (const item of cat.items) {
        for (const grp of (item.optionGroups || [])) {
          if (grp.linkedLibraryId === libraryModId) {
            results.push({
              profileId: p.id,
              profileName: p.name,
              categoryId: cat.id,
              categoryName: cat.nameEN,
              itemId: item.id,
              itemName: item.nameEN,
            });
          }
        }
      }
    }
  }
  return results;
}

/** Count how many products reference a library modifier */
export function countLinkedProducts(libraryModId: string, profiles: MenuProfile[]): number {
  return getLinkedProducts(libraryModId, profiles).length;
}

// ─── Unlink & Make Local ────────────────────────────────────────────────────────

/** Convert a linked option group into a local independent copy */
export function unlinkGroup(group: OptionGroup): OptionGroup {
  return {
    ...group,
    linkedLibraryId: undefined,
    options: group.options.map(o => ({ ...o, libraryOptionId: undefined })),
  };
}
